import styled from 'styled-components';
import { Transforms } from 'slate';
import { useSelected, useFocused, useSlateStatic, ReactEditor } from 'slate-react';
import { ReactNode, useEffect, useState } from 'react';
import i18n from 'src/utils/translate';
import Icon from 'src/components/icon';
import Button from 'src/components/buttons/button';
import { useDispatch, useSelector } from 'src/store/store';
import { showModal } from 'src/modules/shared/modal/actions';
import { ModalType } from 'src/constants/modalTypes';
import { textSmall } from 'styles-js/mixins/typography';
import { ImageElement } from 'src/components/textEditor/customTypes';

// this is just a proposed image element; images are currently outside of the editor.
// expected future work - add images to toolbar, allow multi images, allow images at any position in text
type Props = {
  attributes?: Record<string, string | boolean>,
  element: ImageElement,
  children?: ReactNode,
  imgWithDescription?: boolean,
  setImageNonSlate?: (image: ImageElement) => void,
};
export default function Image({ attributes, children, element, imgWithDescription, setImageNonSlate }: Props) {
  const dispatch = useDispatch();
  const { isAndroidUser } = useSelector(({ context }) => context);

  // slate hooks must be called in the context of a slate component, calling it without it throws a slate error.
  // hooks can be only called from a component or a hook, but - hooks cannot be called conditionally.
  // that's why the name must be a capitalised so it doesn't throw an error when invoked.
  const CallSlateHooks = () => {
    const editor = useSlateStatic();
    const selected = useSelected();
    const focused = useFocused();
    const path = ReactEditor.findPath(editor, element);
    return { editor, path, selected, focused };
  };

  const { editor, path, selected, focused } = !isAndroidUser ? CallSlateHooks() : { editor: null, path: null, selected: false, focused: false };

  const [imageDescription, setNewImgDescription] = useState(element?.description || '');

  const onAddImageDescription = (description: string) => {
    if (imgWithDescription) {
      dispatch(showModal({
        modalType: ModalType.ImageDescription,
        modalProps: {
          imageDescription: description,
          onAccept: (newDescription: string) => setNewImgDescription(newDescription),
        },
      }));
    }
  };

  useEffect(() => {
    // needs to be on update, otherwise the last desc is saved.
    if (editor) {
      Transforms.setNodes(editor, { description: imageDescription }, { at: path });
    } else if (setImageNonSlate) {
      setImageNonSlate({ ...element, description: imageDescription });
    }
  }, [imageDescription]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (element?.url && !element?.description?.length) {
      setNewImgDescription('');
      onAddImageDescription('');
    }
  }, [element?.url]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div {...attributes}> {/* eslint-disable-line react/jsx-props-no-spreading */}
      {children}
      <ImageElement contentEditable={false} data-testid="added-image">
        <ImageWrapper
          alt="image"
          hasShadow={focused && selected}
          src={element.url}
        />
        {(element?.url || element?.hash) && (
          <RemoveButton
            ariaLabel={i18n.t('Remove this image')}
            dataTestId="remove-image-button"
            type="tertiary"
            onClick={() => {
              if (editor) {
                Transforms.removeNodes(editor, { at: path });
              } else if (setImageNonSlate) {
                setImageNonSlate({ type: 'image', file: null, hash: null, url: null, description: null, children: null });
              }
            }}
          >
            {i18n.t('Remove')}{' '}
            <Icon icon="close" />
          </RemoveButton>
        )}
      </ImageElement>
      {imgWithDescription && (element?.url || element?.hash) && (
        <>
          {imageDescription && <Caption contentEditable={false} data-testid="image-description">{imageDescription}</Caption>}

          <Button
            dataTestId="add-image-description-button"
            type="tertiary"
            onClick={() => onAddImageDescription(imageDescription)}
          >
            {i18n.t('Edit description')}
          </Button>
        </>
      )}
    </div>
  );
}

const ImageElement = styled.div`
  position: relative;
  width: fit-content;
  margin-bottom: 15px;
`;

const ImageWrapper = styled.img<{ hasShadow: boolean }>`
  display: block;
  max-width: 100%;
  max-height: 20em;
  box-shadow: ${props => props.hasShadow ? '0 0 0 3px #B4D5FF' : 'none'};
`;

export const RemoveButton = styled(Button)`
  position: absolute;
  top: 10px;
  right: 0;
  margin: 0;
  box-shadow: 0 0 6px ${({ theme }) => theme.colorBlackHover};

  &:focus, &:hover, &:active {
    border-color: ${({ theme }) => theme.colorBlue};
  }
`;

const Caption = styled.p`
  ${textSmall};
  color: ${({ theme }) => theme.colorGreyDark};
`;

